import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/api';
import { useAuthStore } from '../store/auth.store';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { Loader2, ShieldCheck, AlertCircle, ArrowLeft } from 'lucide-react';
import { isSupportedLocale } from '../i18n';

export default function DuoCallback() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { isDesktop } = useBreakpoint();

  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      const duoCode = searchParams.get('duo_code');
      const stateParam = searchParams.get('state');
      const storedState = sessionStorage.getItem('duo_state');
      const redirectUri = sessionStorage.getItem('duo_redirect_uri');

      if (!duoCode || !stateParam) {
        setError(t('auth.duo_callback_missing_params'));
        setIsProcessing(false);
        return;
      }

      if (stateParam !== storedState) {
        setError(t('auth.duo_callback_invalid_state'));
        setIsProcessing(false);
        return;
      }

      if (!redirectUri) {
        setError(t('auth.duo_callback_missing_uri'));
        setIsProcessing(false);
        return;
      }

      try {
        const response = await authApi.duoCallback(duoCode, stateParam, redirectUri);
        const { user, accessToken } = response.data;

        // Clean up sessionStorage
        sessionStorage.removeItem('duo_state');
        sessionStorage.removeItem('duo_redirect_uri');

        setAuth(user, accessToken);
        if (user.language && isSupportedLocale(user.language) && i18n.resolvedLanguage !== user.language) {
          try { await i18n.changeLanguage(user.language); } catch { /* ignore */ }
        }

        // Redirect based on onboarding state
        if (user.role !== 'SUPER_ADMIN' && !user.cguAccepted) {
          navigate('/accept-cgu', { replace: true });
        } else if (user.role !== 'SUPER_ADMIN' && user.mustSetup2FA) {
          navigate('/setup-2fa', { replace: true });
        } else {
          navigate(isDesktop ? '/dashboard' : '/map', { replace: true });
        }
      } catch (err: unknown) {
        const error = err as { response?: { data?: { error?: string } } };
        setError(error.response?.data?.error || t('auth.duo_callback_failed'));
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, setAuth, t, i18n, isDesktop]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mb-4">
          <ShieldCheck className="h-8 w-8 text-primary-600" />
        </div>

        {isProcessing ? (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.duo_callback_title')}</h2>
            <p className="text-gray-500 text-sm mb-8">
              {t('auth.duo_callback_subtitle')}
            </p>
            <div className="flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.duo_callback_error_title')}</h2>
            {error && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm mb-6 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <a
              href="/login"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('auth.back_to_login')}
            </a>
          </>
        )}
      </div>
    </div>
  );
}
